import React, { useEffect, useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Loader2, MessageCircle } from 'lucide-react';

const BotTestChat = ({ botId, botName }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Switching bots in the left panel must start a fresh chat session.
  useEffect(() => {
    setMessages([]);
    setInput('');
    setSessionId(null);
    setLoading(false);
  }, [botId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!botId || !input.trim() || loading) return;

    const userText = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { id: Date.now(), role: 'user', text: userText }]);

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bots/${botId}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: userText,
            session_id: sessionId || undefined,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.reply) {
        throw new Error(data.detail || 'Failed to get reply from bot');
      }
      setSessionId(data.session_id || sessionId);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', text: data.reply },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 2,
          role: 'system',
          text: 'There was an error contacting the bot. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (!botId) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-[11px] text-zinc-500">
        Select a bot from the left to test conversations.
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <MessageCircle className="w-3.5 h-3.5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">Test Bot</h2>
            <p className="text-[11px] text-zinc-500">
              Chat with <span className="text-zinc-200 font-medium">{botName || 'this bot'}</span> using its current
              prompt & knowledge.
            </p>
          </div>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-lg bg-zinc-950/40 flex flex-col h-56">
        <div className="flex-1 px-3 py-2 overflow-y-auto space-y-2 text-[11px]">
          {messages.length === 0 ? (
            <p className="text-zinc-500">
              Start a test conversation by asking a question this bot should know how to answer.
            </p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${
                  m.role === 'user'
                    ? 'justify-end'
                    : m.role === 'assistant'
                    ? 'justify-start'
                    : 'justify-center'
                }`}
              >
                <div
                  className={`max-w-[80%] px-3 py-1.5 rounded-2xl whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-sm'
                      : m.role === 'assistant'
                      ? 'bg-zinc-900 text-zinc-100 rounded-bl-sm border border-zinc-800'
                      : 'bg-transparent text-zinc-500 text-[10px]'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))
          )}
        </div>
        <form
          onSubmit={handleSend}
          className="border-t border-zinc-800 px-2 py-2 flex items-center gap-2 bg-zinc-950/80"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message to test the bot..."
            className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-emerald-500"
          />
          <Button
            type="submit"
            size="sm"
            className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500"
            disabled={loading || !input.trim()}
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BotTestChat;
